import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from "react";
import { Button, ConfirmDialog } from "@project-mgmt/ui";
import { useConvertToProject } from "../hooks/useConvertToProject";
/**
 * Button component for converting successful ventures to projects
 *
 * Requirements:
 * - 8.1: Verify venture status is 'successful'
 * - 8.2: Create new project with venture data
 * - 8.3: Update venture status to 'converted'
 * - 8.4: Add all backers as workspace members
 * - 8.5: Link project to venture via venture_id
 *
 * Features:
 * - Only shows for successful ventures
 * - Verifies user is creator or workspace admin
 * - Confirmation dialog before conversion
 * - Loading state during conversion
 * - Navigates to new project on success
 * - Mobile-friendly with large touch targets
 * - Accessible with ARIA labels
 */
export const ConvertToProjectButton = ({ venture, currentUserId, workspaceId, onSuccess, notify, className = "", }) => {
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    // Hook for conversion logic
    const { convertToProject, isConverting } = useConvertToProject(venture.id, workspaceId, notify);
    // Check if user is the creator
    const isCreator = currentUserId === venture.createdBy;
    // Check if venture can be converted
    const canConvert = venture.status === "successful" && isCreator;
    // Don't render if user can't convert
    if (!canConvert) {
        return null;
    }
    // Handle conversion confirmation
    const handleConfirm = async () => {
        const result = await convertToProject();
        if (result.success && result.projectId) {
            setShowConfirmDialog(false);
            onSuccess?.(result.projectId);
        }
        // Error handling is done in the hook with notifications
    };
    return (_jsxs(_Fragment, { children: [_jsx(Button, { onClick: () => setShowConfirmDialog(true), disabled: isConverting, className: `bg-green-600 font-medium text-white transition-colors hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 ${className}`, size: "lg", "aria-label": "Convert venture to project", children: isConverting ? (_jsxs("span", { className: "flex items-center justify-center gap-2", children: [_jsxs("svg", { className: "h-5 w-5 animate-spin", xmlns: "http://www.w3.org/2000/svg", fill: "none", viewBox: "0 0 24 24", "aria-hidden": "true", children: [_jsx("circle", { className: "opacity-25", cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "4" }), _jsx("path", { className: "opacity-75", fill: "currentColor", d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" })] }), "Converting..."] })) : (_jsxs(_Fragment, { children: [_jsx("svg", { className: "mr-2 inline-block h-5 w-5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", "aria-hidden": "true", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" }) }), "Convert to Project"] })) }), _jsx(ConfirmDialog, { isOpen: showConfirmDialog, onClose: () => setShowConfirmDialog(false), onConfirm: handleConfirm, title: "Convert Venture to Project?", message: `This will create a new project from "${venture.name}" and add all ${venture.pledgeCount} backer(s) as workspace members. This action cannot be undone.`, confirmText: "Convert to Project", cancelText: "Cancel", variant: "info", isLoading: isConverting })] }));
};
